import { Reorder, useDragControls } from 'framer-motion';
import { FileText, GripVertical, Pencil, Trash2, Video } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CourseLessonTreeNode } from '../../../../../../lib/interfaces/courses';

const MIN_TITLE_LENGTH = 2;
const MAX_TITLE_LENGTH = 120;

export interface LessonRowProps {
  /** Dotted display index within the module, e.g. "1.2" */
  index: string;
  lesson: CourseLessonTreeNode;
  /** When true, row is rendered as a sortable framer Reorder.Item (drag handle active). */
  sortable?: boolean;
  onTitleChange: (lessonId: string, title: string) => void;
  onEdit: (lesson: CourseLessonTreeNode) => void;
  onDelete: (lesson: CourseLessonTreeNode) => void;
}

const LessonRow = ({ index, lesson, sortable = false, onTitleChange, onEdit, onDelete }: LessonRowProps) => {
  const [title, setTitle] = useState(lesson.title);
  const [error, setError] = useState<string | null>(null);
  const lastSubmittedRef = useRef(lesson.title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTitle(lesson.title);
    lastSubmittedRef.current = lesson.title;
    setError(null);
  }, [lesson.title]);

  const handleTitleChange = (next: string) => {
    setTitle(next);
    if (error) setError(null);
  };

  const handleTitleBlur = () => {
    const trimmed = title.trim();
    if (trimmed === lastSubmittedRef.current) {
      setTitle(lastSubmittedRef.current);
      setError(null);
      return;
    }
    if (trimmed.length < MIN_TITLE_LENGTH) {
      setError(`Mindestens ${MIN_TITLE_LENGTH} Zeichen.`);
      // Stay focused so the user can fix it.
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    lastSubmittedRef.current = trimmed;
    setError(null);
    onTitleChange(lesson._id, trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setTitle(lastSubmittedRef.current);
      setError(null);
      (e.target as HTMLInputElement).blur();
    }
  };

  const dragControls = useDragControls();

  const row = (
    // Hierarchy comes from the rail in ModuleRow, not from a fill — keeps grey on the badges only.
    <div className="rounded-lg flex items-center gap-3 px-2 py-2 hover:bg-[#F0F4FC] transition-colors">
      {sortable && (
        <button
          type="button"
          aria-label="Verschieben"
          onPointerDown={(e) => dragControls.start(e)}
          className="text-[#888] hover:text-[#12282A] cursor-grab active:cursor-grabbing touch-none">
          <GripVertical className="w-4 h-4" />
        </button>
      )}

      <span className="text-[13px] font-semibold text-[#12282A] shrink-0">{index}</span>

      <div className="flex-1 min-w-0 flex flex-col">
        <input
          ref={inputRef}
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          onBlur={handleTitleBlur}
          onKeyDown={handleKeyDown}
          maxLength={MAX_TITLE_LENGTH}
          aria-invalid={!!error}
          className={`bg-transparent text-[14px] font-semibold text-[#12282A] outline-none border-b py-0.5 px-1 transition-colors ${
            error ? 'border-red-400' : 'border-transparent focus:border-[#5FD5DE]'
          }`}
          placeholder="Name der Lektion"
        />
        {error && <span className="text-[11px] text-red-500 px-1">{error}</span>}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {lesson.hasVideo && (
          <span className="text-[11px] font-medium px-2 py-1 rounded-full bg-[#F0F4FC] text-[#12282A] flex items-center gap-1" title="Enthält ein Video">
            <Video className="w-3 h-3" />
            Video
          </span>
        )}
        {lesson.hasText && (
          <span className="text-[11px] font-medium px-2 py-1 rounded-full bg-[#F0F4FC] text-[#12282A] flex items-center gap-1" title="Enthält Text">
            <FileText className="w-3 h-3" />
            Text
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => onEdit(lesson)}
          aria-label="Lektion bearbeiten"
          className="w-7 h-7 rounded-full border border-[#E4E7EC] bg-white hover:bg-[#F0F4FC] flex items-center justify-center text-[#12282A] shrink-0">
          <Pencil className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={() => onDelete(lesson)}
          aria-label="Lektion löschen"
          className="w-7 h-7 rounded-full border border-[#E4E7EC] bg-white hover:bg-red-50 hover:border-red-300 flex items-center justify-center text-red-500 shrink-0">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );

  if (!sortable) return row;

  return (
    <Reorder.Item value={lesson} dragListener={false} dragControls={dragControls} as="div">
      {row}
    </Reorder.Item>
  );
};

export default LessonRow;
